/**
 * The `window.wone.desktop` contract, as this app consumes it.
 *
 * The canonical contract lives in `frontend/packages/sdk/src/desktop-bridge.ts`
 * and the desktop shell implements it there. This app cannot import it: the
 * `@wone/sdk` package is a Bun workspace member and this app is a standalone
 * yarn v1 Create React App, so the two dependency trees do not meet. The shape
 * below is therefore a deliberate copy of the members this app uses, and only
 * those.
 *
 * Keeping the copy narrow is what makes the duplication safe. Adding a member
 * here means the shell must already provide it; the shell's own types are the
 * ones that decide.
 */

export interface DesktopNotification {
  id: string;
  title: string;
  body: string;
  /** Where a click should land, as an app-relative route or a deep link. */
  deepLink?: string;
}

/** Sent when a Pomodoro break begins; `breakMs` is the longest session worth offering. */
export interface BreakStartingMessage {
  requestId: string;
  breakMs: number;
  phase: "break" | "long_break";
}

export interface BreakSession {
  id: string;
  title: string;
  durationSeconds: number;
  /** An app-relative route. */
  deepLink: string;
}

export interface BreakSessionReply {
  requestId: string;
  session: BreakSession | null;
}

/** The shell's editable settings. Minutes are whole numbers within the shell's bounds. */
export interface DesktopPomodoroSettings {
  focusMinutes: number;
  breakMinutes: number;
  longBreakMinutes: number;
  longBreakEvery: number;
  meetingGraceMinutes: number;
  idleMinutes: number;
  preMeetingBufferMinutes: number;
}

export interface DesktopSettings {
  pomodoro: DesktopPomodoroSettings;
  /** `calendar` applies on macOS only, `camera` on Windows only. */
  presenceProviders: { calendar: boolean; camera: boolean };
  launchAtLogin: boolean;
}

export interface DesktopSettingsPatch {
  pomodoro?: Partial<DesktopPomodoroSettings>;
  presenceProviders?: Partial<DesktopSettings["presenceProviders"]>;
  launchAtLogin?: boolean;
}

export interface WoneDesktopBridge {
  platform: "darwin" | "win32" | "linux";
  /** The shell's own version, not the renderer's. */
  version: string;
  env: "staging" | "production" | "development";
  openExternal(url: string): Promise<void>;
  /**
   * The OS keychain, limited by the shell to one key: the refresh token from
   * a desktop SSO sign-in (see `sso.ts`). Optional because shells before this
   * one do not provide it, and an older shell must still boot this bundle.
   */
  secureStorage?: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
    remove(key: string): Promise<void>;
  };
  /** Custom-scheme URLs the OS routed to this app. Returns its unsubscribe. */
  onDeepLink(callback: (url: string) => void): () => void;
  /**
   * Tells the shell this renderer is subscribed, which releases any link the
   * OS delivered before the bundle finished loading. Optional because it
   * postdates the first shell release, so it is always called as
   * `signalReady?.()`.
   */
  signalReady?(): void;
  notify(notification: DesktopNotification): Promise<void>;
  /** Fires when the user activates a notification this app posted. Returns
   *  its unsubscribe. */
  onNotificationClick(
    callback: (id: string, deepLink?: string) => void
  ): () => void;
  /**
   * The Pomodoro break handshake: answer every `onBreakStarting` with
   * `replyBreakSession`, with a null session if need be. Optional because
   * shells before the timer do not provide it.
   */
  pomodoro?: {
    onBreakStarting(
      callback: (message: BreakStartingMessage) => void
    ): () => void;
    replyBreakSession(reply: BreakSessionReply): Promise<void>;
  };
  /**
   * The shell's settings, edited from the learner Settings page. `update`
   * rejects a value out of range with the reason. Optional because shells
   * before the settings screen do not provide it.
   */
  settings?: {
    get(): Promise<DesktopSettings>;
    update(patch: DesktopSettingsPatch): Promise<DesktopSettings>;
    onChange(callback: (settings: DesktopSettings) => void): () => void;
  };
}

declare global {
  // eslint-disable-next-line no-var
  var wone: { desktop?: WoneDesktopBridge } | undefined;
}

/**
 * The bridge if this bundle is running inside the desktop shell, `null` in a
 * browser.
 *
 * Read on every call rather than captured once at module load, so a test can
 * install and remove a stub without reloading the module. In the real app the
 * preload script runs before any app code, so the answer never changes
 * mid-session either way.
 */
export function getDesktopBridge(): WoneDesktopBridge | null {
  return globalThis.wone?.desktop ?? null;
}
